<script lang="ts" setup>
import type { ChicletBackgroundColor } from '@@/bits/chiclet_color'
import { __ } from '@@/bits/intl'
import OzAvatar from '@@/library/v4/components/OzAvatar.vue'
import OzBadge, { OzBadgeColorScheme } from '@@/library/v4/components/OzBadge.vue'
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    iconName?: string
    color?: ChicletBackgroundColor
    disabled?: boolean
    avatarUrl?: string
    avatarAltText?: string
    xNewBadge?: boolean
    text?: string
    subtext?: string
  }>(),
  {
    iconName: '',
    color: 'green',
    disabled: false,
    avatarUrl: '',
    avatarAltText: '',
    xNewBadge: false,
    text: '',
    subtext: '',
  },
)
defineEmits<{
  (name: 'click', event: MouseEvent): void
}>()

const iconColorClasses = computed(() => {
  switch (props.color) {
    case 'green':
      return ['text-park']
    case 'orange':
      return ['text-tangerine']
    case 'pink':
      return ['text-padlet-pink']
    case 'blue':
      return ['text-oceanic']
    case 'purple':
      return ['text-grape']
    case 'teal':
      return ['text-teal']
    default:
      return []
  }
})
</script>

<template>
  <OzBaseButton
    :class="[
      'relative',
      'flex',
      'items-center',
      'text-dark-text-100',
      'dark:text-light-text-100',
      'py-3 @tablet-portrait/surface-container:py-2 tablet-portrait:-@:py-2',
      'ps-3 pe-0',

      // Background
      'dark:bg-dark-ui-200',
      color === 'green' && ['bg-park-0'],
      color === 'orange' && ['bg-tangerine-0'],
      color === 'pink' && ['bg-padlet-pink-0'],
      color === 'blue' && ['bg-oceanic-0'],
      color === 'purple' && ['bg-grape-0'],
      color === 'teal' && ['bg-teal-0'],

      // Interaction
      !disabled && [
        'transition-all',

        // Animation
        'hhover:animate-pulse-bg-color',
        'hactive:animate-none',
        'focus-visible:animate-none',
        'dark:pulse-bg-from-dark-ui-200',
        'dark:pulse-bg-to-dark-ui-300',

        // Hover/Focus/Active
        'hactive:text-dark-text-100',
        'hactive:dark:bg-dark-ui-300',
        'hactive:dark:text-light-text-100',
        'focus-visible:dark:bg-dark-ui-300',
        'dark:active:bg-dark-ui-300',
        'dark:active:text-light-text-100',
        'dark:focus-visible:bg-dark-ui-300',
        'active:text-dark-text-100',

        // Focus ring
        '@tablet-portrait/surface-container:focus-visible:ring-[2.5px] tablet-portrait:-@:focus-visible:ring-[2.5px]',
        '@tablet-portrait/surface-container:ring-offset-[1.5px] tablet-portrait:-@:ring-offset-[1.5px]',
        'dark:ring-offset-grey-900 ring-offset-white',
        'ring-grape-500 dark:ring-canary-500',

        color === 'green' && [
          'focus-visible:bg-park-200',
          'hactive:bg-park-200',
          'pulse-bg-from-park-0 pulse-bg-to-park-200',
        ],
        color === 'orange' && [
          'focus-visible:bg-tangerine-100',
          'hactive:bg-tangerine-100',
          'pulse-bg-from-tangerine-0 pulse-bg-to-tangerine-200',
        ],
        color === 'pink' && [
          'focus-visible:bg-padlet-pink-100',
          'hactive:bg-padlet-pink-100',
          'pulse-bg-from-padlet-pink-0 pulse-bg-to-padlet-pink-200',
        ],
        color === 'blue' && [
          'focus-visible:bg-oceanic-100',
          'hactive:bg-oceanic-100',
          'pulse-bg-from-oceanic-0 pulse-bg-to-oceanic-200',
        ],
        color === 'purple' && [
          'focus-visible:bg-grape-100',
          'hactive:bg-grape-100',
          'pulse-bg-from-grape-0 pulse-bg-to-grape-200',
        ],
        color === 'teal' && [
          'focus-visible:bg-teal-100',
          'hactive:bg-teal-100',
          'pulse-bg-from-teal-0 pulse-bg-to-teal-200',
        ],
      ],
    ]"
    @click="$emit('click', $event)"
  >
    <OzIcon v-if="iconName" :size="24" :class="['shrink-0', iconColorClasses]" :name="iconName" />
    <OzAvatar
      v-else-if="avatarUrl"
      :height="24"
      :width="24"
      :src="avatarUrl"
      :alt-text-name="avatarAltText"
      :class="['shrink-0 rounded-full', iconColorClasses]"
    />

    <span
      :class="[
        'mx-3 text-body-posts @tablet-portrait/surface-container:text-body-small tablet-portrait:-@:text-body-small text-start truncate grow',
        subtext && 'max-w-[60%] shrink-0',
      ]"
      >{{ text }}</span
    >

    <span v-if="subtext" class="ms-auto me-4 text-body-small text-dark-text-200 dark:text-light-text-200 truncate">{{
      subtext
    }}</span>
    <OzBadge
      v-if="xNewBadge"
      :text="__('New')"
      :color-scheme="OzBadgeColorScheme.Bare"
      :class="[
        'text-light-text-100',
        color === 'green' && ['bg-park-500 dark:bg-park-800'],
        color === 'orange' && ['bg-tangerine-500 dark:bg-tangerine-800'],
        color === 'blue' && ['bg-oceanic-500 dark:bg-oceanic-800'],
        color === 'purple' && ['bg-grape-500 dark:bg-grape-800'],
        'me-4 @tablet-portrait/surface-container:me-2.5 tablet-portrait:-@:me-2.5',
        'px-1.5 h-5',
        'rounded-md',
      ]"
    />
  </OzBaseButton>
</template>
